/**
 * Friendly error toast helper.
 *
 * Use everywhere instead of toast.error(err.response?.data?.detail || 'Failed').
 * It:
 *   • Always prefers the server's `detail` message (which is now user-friendly
 *     across our newer endpoints — see student_context/decisions/roadmap_v2/etc).
 *   • Translates raw HTTP status codes (401/403/404/500) into clear sentences.
 *   • Filters out dev-speak like "not your child", "no access", "decision not
 *     found" with proper human-readable copies in case any legacy endpoint
 *     still returns those.
 *   • Optionally swallows the toast for known-empty states (e.g. 404 on a
 *     listing the user simply hasn't created yet) when `quietOn404` is passed.
 */
import { toast } from 'sonner';

const RAW_DEV_PHRASES = new Map([
  ['not your child', "You don't have access to this student's profile."],
  ['no access', "You don't have permission to view this."],
  ['no user id', 'Your session expired. Please sign in again.'],
  ['parents only', 'This feature is for parent accounts.'],
  ['parents cannot edit decisions', 'Only the student can edit their decisions.'],
  ['parents cannot add milestones', 'Only the student can add roadmap milestones.'],
  ['parents cannot complete milestones', 'Only the student can mark milestones complete.'],
  ['decision not found', "We couldn't find that decision."],
  ['milestone not found', "We couldn't find that milestone."],
  ['student not found', "We couldn't find that student account."],
  ['no linked child', 'Connect a student to your account first to use this feature.'],
]);

function humanize(message) {
  if (!message) return null;
  const lower = String(message).toLowerCase();
  for (const [needle, friendly] of RAW_DEV_PHRASES.entries()) {
    if (lower.includes(needle)) return friendly;
  }
  return message;
}

const STATUS_FALLBACKS = {
  400: 'That request looked off. Please check the form and try again.',
  401: 'Your session expired. Please sign in again.',
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: 'That action conflicts with the current state. Refresh and try again.',
  413: 'That file is too large. Try a smaller one.',
  422: 'Some of the details look incomplete. Please review and try again.',
  429: "You're moving fast — please slow down a moment and try again.",
  500: 'Something went wrong on our side. Please try again in a moment.',
  502: 'Our AI service is briefly unavailable. Please try again shortly.',
  503: 'The service is busy right now. Please try again in a moment.',
  504: "That took too long. Please try again.",
};

/**
 * @param {Error|unknown} err — typically the axios error
 * @param {object} options
 *   - fallback {string}   — message if no useful detail can be extracted
 *   - quietOn404 {bool}   — don't show a toast on 404 (use for empty-state pages)
 *   - context {string}    — short label, prepended to the friendly message
 */
export function showFriendlyError(err, options = {}) {
  const { fallback = 'Something went wrong. Please try again.',
          quietOn404 = false,
          context = '' } = options;

  const status = err?.response?.status;
  if (quietOn404 && status === 404) return null;

  const raw = err?.response?.data?.detail
    || err?.response?.data?.message
    || err?.message;
  const humanized = humanize(raw) || STATUS_FALLBACKS[status] || fallback;
  const finalMessage = context ? `${context}: ${humanized}` : humanized;

  toast.error(finalMessage);
  return finalMessage;
}

/** Use when you want to gracefully handle an empty listing without a toast. */
export function isExpectedEmpty(err) {
  const s = err?.response?.status;
  return s === 404 || s === 204;
}

/** Show a friendly success toast (kept here for symmetry with the error helper). */
export function showFriendlySuccess(message) {
  toast.success(message || 'Done.');
}
